<template>
  <v-container class="px-0 pb-5 tracker-entry-page" style="background-color:#F5F5F5;min-height:100vh;">

    <!-- Loading State -->
    <v-sheet v-if="isLoading" class="mx-6 pa-4 border-rounded text-center">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-2">Loading tracker entries...</p>
    </v-sheet>

    <!-- Main Content -->
    <div v-else>

      <!-- Form -->
      <v-form @submit.prevent="submitEntries">
        <!-- Tracker Entries List -->
        <div v-if="trackerEntries.length > 0">
          <v-sheet 
            v-for="(entry, index) in trackerEntries" 
            :key="`entry-${index}`"
            class="mx-6 pa-4 border-rounded mb-4"
          >
            <div class="mb-3">
              <h5 class="text-subtitle-2 font-weight-500">{{ entry.tracker_template_item?.name || 'Tracker Entry' }}</h5>
            </div>

            <!-- Emoji scale -->
            <div v-if="entry.tracker_template?.scale_type === 'emoji'" class="emoji-scale">
              <button
                v-for="(step, stepIndex) in (entry.tracker_template?.scale_steps || [])"
                :key="stepIndex"
                type="button"
                class="emoji-step-btn"
                :class="{ 'emoji-step-btn--selected': entry.value === stepIndex + 1 }"
                :style="entry.value === stepIndex + 1 ? { borderColor: settingsStore.settings.primaryColor, backgroundColor: settingsStore.settings.primaryColor + '18' } : {}"
                :disabled="isReadonly"
                @click="entry.value = stepIndex + 1"
              >
                <span class="emoji-step-label">{{ step }}</span>
              </button>
            </div>

            <!-- Numeric scale -->
            <template v-else>
              <div class="mb-2">
                <RatingSlider
                  v-model="entry.value"
                  :color="settingsStore.settings.primaryColor"
                  :min="entry.tracker_template?.scale_min || 0"
                  :max="entry.tracker_template?.scale_max || 10"
                  :disabled="isReadonly"
                />
              </div>
              <div class="d-flex justify-space-between mt-0">
                <span class="text-caption text-grey-darken-1">{{ entry.tracker_template?.scale_min || 0 }}</span>
                <span class="text-caption text-grey-darken-1">{{ entry.tracker_template?.scale_max || 10 }}</span>
              </div>
            </template>
          </v-sheet>
        </div>

        <!-- No Entries Message -->
        <v-sheet v-else class="mx-6 pa-4 border-rounded text-center">
          <v-icon size="48" color="grey-lighten-1" class="mb-3">fa fa-calendar-xmark</v-icon>
          <p class="text-h6 text-grey-darken-1">
            {{ isReadonly ? 'No entries recorded for this date' : 'No tracker entries for this date' }}
          </p>
          <p class="text-body-2 text-grey-darken-1 mt-2">
            {{ isReadonly 
              ? `No tracker data was recorded for ${moment(entryDate).format('DD MMM YYYY')}.`
              : `There are no tracker entries scheduled for ${entryDate}.`
            }}
          </p>
        </v-sheet>

        <!-- Error Alert -->
        <v-alert
          v-if="formError"
          type="error"
          class="mx-6 mb-4"
        >
          {{ formError }}
        </v-alert>

        <!-- Submit Button -->
        <div v-if="trackerEntries.length > 0 && !isReadonly" class="mx-6 mb-5">
          <v-btn
            type="submit"
            :color="settingsStore.settings.primaryColor"
            :disabled="!isFormValid || isSubmitting"
            :loading="isSubmitting"
            class="w-100 py-3 text-white submit-btn"
            size="large"
            elevation="0"
          >
            Submit
          </v-btn>
        </div>
      </v-form>
    </div>
  </v-container>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSettingsStore } from '@/api/store/settings';
import { healthTrackerService, type TrackerEntry } from "@/api/services/healthTracker";
import moment from "moment";
import RatingSlider from "@/components/custom/RatingSlider.vue";

const router = useRouter();
const route = useRoute();
const settingsStore = useSettingsStore();

const isLoading = ref(true);
const isSubmitting = ref(false);
const formError = ref<string | null>(null);

const trackerId = ref<number>(0);
const entryDate = ref<string>('');
const trackerEntries = ref<any[]>([]); // Use any[] for now, or define a local type matching the API response

const isReadonly = computed(() => route.query.readonly === 'true');

const isFormValid = computed(() => {
  return trackerEntries.value.length > 0 &&
    trackerEntries.value.every(entry => {
      if (entry.value === undefined || entry.value === null) return false;
      if (entry.tracker_template?.scale_type === 'emoji') {
        const stepCount = entry.tracker_template?.scale_steps?.length ?? 0;
        return entry.value >= 1 && entry.value <= stepCount;
      }
      return (
        entry.value >= (entry.tracker_template?.scale_min ?? 0) &&
        entry.value <= (entry.tracker_template?.scale_max ?? 10)
      );
    });
});

const formattedDate = computed(() => {
  return moment(entryDate.value).format('dddd, DD MMMM YYYY');
});

const loadTrackerEntries = async () => {
  try {
    isLoading.value = true;
    trackerId.value = parseInt(route.params.id as string);
    entryDate.value = route.params.date as string;

    if (!trackerId.value || !entryDate.value) {
      formError.value = 'Invalid tracker ID or date';
      return;
    }

    try {
      // Get entries for the specific date
      const entries = await healthTrackerService.getTrackerEntriesByDate(trackerId.value, entryDate.value);
      trackerEntries.value = entries.map((entry: any) => ({
        ...entry,
        value: entry.value ?? (entry.tracker_template?.scale_min ?? 0),
        notes: entry.notes ?? '',
      }));
    } catch (entryError) {
      // If no entries exist for this date, show empty state
      console.log('No entries found for date:', entryDate.value);
      trackerEntries.value = [];
    }
  } catch (error) {
    console.error('Error loading tracker entries:', error);
    formError.value = 'Failed to load tracker entries. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

const submitEntries = async () => {
  if (!isFormValid.value || !trackerId.value) return;
  isSubmitting.value = true;
  formError.value = null;

  try {
    for (const entry of trackerEntries.value) {
      const entryData = {
        entry_datetime: entry.entry_datetime,
        tracker_id: trackerId.value,
        tracker_template_id: entry.tracker_template_id,
        item_id: entry.item_id,
        value: entry.value,
        notes: entry.notes ?? '',
      };
      if (entry.id) {
        await healthTrackerService.updateEntry(trackerId.value, entry.id, entryData);
      } else {
        await healthTrackerService.createEntry(trackerId.value, entryData);
      }
    }
    router.push({ name: 'health-suite-tracker' });
  } catch (err: any) {
    formError.value = err.response?.data?.message || 'Failed to save entries. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  loadTrackerEntries();
});
</script>

<style lang="scss" scoped>
.tracker-entry-page {
  background-color: #F5F5F5;
  min-height: 100vh;
}

.border-rounded {
  border-radius: 16px;
}

.primary-color {
  color: v-bind('settingsStore.settings.primaryColor');
}

.font-weight-600 {
  font-weight: 600;
}

.font-weight-500 {
  font-weight: 500;
}

:deep(.v-field) {
  border-radius: 12px !important;
}

:deep(.v-btn) {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
  border-radius: 12px;
}

/* Add text input for comments */
.v-sheet {
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.05);
  border-radius: 16px;
}

.entry-card {
  background-color: white;
  padding-bottom: 16px !important;
}

/* Add comment field */
.comment-field {
  margin-top: 0;
  border-radius: 8px;
  background-color: #F9F9F9;
  padding: 8px 12px;
  font-size: 14px;
  color: #757575;
  border: 1px solid #EEEEEE;
}

:deep(.comment-field .v-field__input) {
  padding: 0;
  font-size: 14px;
  color: #757575;
  min-height: 36px;
}

:deep(.comment-field .v-field__input::placeholder) {
  color: #BBBBBB;
  opacity: 1;
}

.submit-btn {
  border-radius: 24px;
  font-weight: 500;
  font-size: 16px;
  height: 56px;
}

.emoji-scale {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.emoji-step-btn {
  flex: 1;
  min-width: 56px;
  padding: 10px 6px;
  border: 2px solid #E0E0E0;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s, transform 0.1s;
  text-align: center;
  font-size: 13px;
  line-height: 1.3;
  word-break: break-word;

  &:hover:not(:disabled) {
    border-color: #BDBDBD;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
}

.emoji-step-btn--selected {
  font-weight: 600;
}

.emoji-step-label {
  display: block;
  pointer-events: none;
}
</style> 